import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import UserIdentity from '../../molecules/user-identity/user-identity';
import UserSphere from '../../molecules/user-sphere/user-sphere';
import './metasphere-nav-bar.scss';
import MetaspaceMenu from '../../molecules/MetaspaceMenu/MetaspaceMenu';
import PerspectiveMenu from '../../molecules/PerspectiveMenu/PerspectiveMenu';
import StatusSelector from '../../molecules/StatusSelector/StatusSelector';
import type { ContextOption } from '../../molecules/ContextSelectorMenu/ContextSelectorMenuMolecule';
import ContextSelectorMenuMolecule from '../../molecules/ContextSelectorMenu/ContextSelectorMenuMolecule';
import GlobalSearchResultsListMolecule, {
  SearchResultSphere,
} from '../../molecules/GlobalSearch/GlobalSearchResultsListMolecule';
import { Sphere } from 'types/global';

interface MetasphereNavBarProps {
  sphere: Sphere;
  onSphereChange: (newSphereId: string) => void;
  defaultLanguage: string;
  onLanguageChange: (lang: string) => void;
  metaspaceId: string;
  /** Callback when user wants to configure status factors - opens SphereEditor to Status Factors tab */
  onConfigureStatusFactors?: (sphereId: string) => void;
  /** Available contexts for the context selector */
  contexts?: ContextOption[];
  /** Whether context list is loading */
  contextLoading?: boolean;
  /** Reports which sphere the nav bar is fully populated for — i.e. the identity
   *  selector has resolved AND the breadcrumb has loaded *this* sphere. Null otherwise.
   *  The page uses this to hold the metaspace back until the header has caught up. */
  onReadyChange?: (readySphereId: string | null) => void;
}

const MetasphereNavBar: React.FC<MetasphereNavBarProps> = ({
  sphere,
  onSphereChange,
  metaspaceId,
  onConfigureStatusFactors,
  contexts,
  contextLoading,
  onReadyChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiClients = useApiClientsOrNull();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const [selectedPerspective, setSelectedPerspective] = useState<Sphere | null>(
    null
  );

  // The nav bar is ready *for a specific sphere*: the identity selector has resolved and
  // the breadcrumb has loaded this exact sphere. Keying on the sphere (rather than a
  // boolean) is what stops a leftover "ready" from the previous user being read as
  // readiness for the new one — which let the metaspace mount, then tear down and
  // reload once the header finally caught up.
  const [identityLoading, setIdentityLoading] = useState(true);
  const [breadcrumbSphereId, setBreadcrumbSphereId] = useState<string | null>(null);
  const navReadySphereId =
    !identityLoading && breadcrumbSphereId === sphere.id ? sphere.id : null;
  useEffect(() => {
    onReadyChange?.(navReadySphereId);
  }, [navReadySphereId, onReadyChange]);

  // Search state
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultSphere[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedResultIndex, setSelectedResultIndex] = useState(0);

  // Context selector state
  const [selectedSearchContext, setSelectedSearchContext] = useState<string | null>(null);

  // Search handlers
  const performSearch = useCallback(
    async (query: string, contextSystemName: string | null = selectedSearchContext) => {
      if (!apiClients) {
        setSearchError(t('globalSearch.error'));
        return;
      }

      setIsSearching(true);
      setSearchError(null);

      try {
        const payload: any = {
          query: query.trim(),
          limit: 30,
          offset: 0,
        };

        // Look up context filter expressions and send them to the search API
        if (contextSystemName && contexts) {
          const ctx = contexts.find(c => c.systemName === contextSystemName);
          if (ctx?.allLevelPropertyFilters && ctx.allLevelPropertyFilters.length > 0) {
            payload.contextLevelFilters = ctx.allLevelPropertyFilters;
          } else if (ctx?.level0PropertyFilters && ctx.level0PropertyFilters.length > 0) {
            payload.contextFilters = ctx.level0PropertyFilters;
          }
        }

        const response = await apiClients.searchClient.post('/search', payload);

        const data = response.data || response;
        const results = data.results || [];

        setSearchResults(results);
        setSelectedResultIndex(0);
      } catch (error) {
        console.error('Global search failed:', error);
        setSearchError(t('globalSearch.error'));
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [apiClients, t, selectedSearchContext, contexts]
  );

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (!query || query.trim() === '') {
        setSearchResults([]);
        setIsSearching(false);
        setSearchError(null);
        return;
      }

      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    },
    [performSearch]
  );

  const handleSearchIconClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    // Trigger ripple effect
    const button = event.currentTarget;
    button.classList.add('ripple-active');
    setTimeout(() => button.classList.remove('ripple-active'), 600);

    if (isSearchExpanded) {
      // Collapsing - trigger closing animations
      const container = searchContainerRef.current;
      if (container) {
        container.classList.add('nav-bar-search-container--closing');

        // Wait for closing animations to complete (longest delay 0.3s + animation 0.3s = 600ms)
        setTimeout(() => {
          container.classList.remove('nav-bar-search-container--closing');
          setIsSearchExpanded(false);
          setSearchQuery('');
          setSearchResults([]);
          setSearchError(null);
        }, 600);
      }
    } else {
      // Expanding - focus will be handled by autoFocus in input
      setIsSearchExpanded(true);
    }
  }, [isSearchExpanded]);

  const handleContextSelect = useCallback((systemName: string | null) => {
    setSelectedSearchContext(systemName);
    // Re-run search with new context if there's a query
    if (searchQuery.trim()) {
      performSearch(searchQuery, systemName);
    }
  }, [searchQuery, performSearch]);

  const handleResultSelect = useCallback(
    (searchResult: SearchResultSphere) => {
      const sphereId = searchResult.id;
      navigate(`/sphere/${sphereId}/metaspace/${metaspaceId}`, { replace: true });

      // Close search
      setIsSearchExpanded(false);
      setSearchQuery('');
      setSearchResults([]);
      setSearchError(null);
    },
    [navigate, metaspaceId]
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedResultIndex((prev) => Math.min(prev + 1, searchResults.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedResultIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (searchResults[selectedResultIndex]) {
            handleResultSelect(searchResults[selectedResultIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsSearchExpanded(false);
          setSearchQuery('');
          setSearchResults([]);
          setSearchError(null);
          break;
      }
    },
    [searchResults, selectedResultIndex, handleResultSelect]
  );

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if click is on context selector menu (which is rendered to document.body)
      const isContextMenuClick = target.closest('.context-selector-menu__popup') ||
                                 target.closest('.context-selector-menu');

      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(target) &&
        !isContextMenuClick &&
        isSearchExpanded
      ) {
        setIsSearchExpanded(false);
        setSearchQuery('');
        setSearchResults([]);
        setSearchError(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchExpanded]);

  // Cleanup search timeout
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleMetaspaceSelected = useCallback(
    (selectedMetaspaceId: string) => {
      navigate(`/sphere/${sphere.id}/metaspace/${selectedMetaspaceId}`);
    },
    [navigate, sphere]
  );

  const handlePerspectiveSelected = useCallback(
    (perspectiveId: string) => {
      if (perspectiveId) {
        const thePerspective = sphere.perspectives?.find(
          (perspective) => perspective.id === perspectiveId
        );
        if (thePerspective) {
          setSelectedPerspective(thePerspective);
        }
      }
    },
    [sphere]
  );
  const buildInitialPath = (sphere: Sphere): string => {
    const ancestorNames: string[] = [];
    let current = sphere;
    while ((current?.ancestors ?? []).length > 0) {
      const parent = current.ancestors?.[0];
      if (!parent) break;
      ancestorNames.push(parent.properties?.name);
      current = parent;
    }
    ancestorNames.reverse();
    return [...ancestorNames, sphere.properties?.name].join(' / ');
  };
  return (
    <div className="grid metssphere-nav-bar">
      <div className="nav-bar-row">
        <UserIdentity identityId={sphere.id} onLoadingChange={setIdentityLoading} urlSphere={sphere} />
        <div className="metatool-layer">
          <div className="left-section">
            <UserSphere
              sphereId={sphere.id}
              metaspaceId={metaspaceId}
              onSphereChange={onSphereChange}
              initialSelectedPath={buildInitialPath(sphere)}
              contexts={contexts}
              contextLoading={contextLoading}
              onLoadedSphereChange={setBreadcrumbSphereId}
            />
          </div>
          <div className="right-section">
            {/* Integrated global search - first icon in right section */}
            <div
              ref={searchContainerRef}
              className={`nav-bar-search-container ${isSearchExpanded ? 'nav-bar-search-container--expanded' : ''}`}
            >
              {isSearchExpanded ? (
                <>
                  <div className="nav-bar-search-input-container">
                    <i className="pi pi-search nav-bar-search-input-icon" />
                    {contexts && contexts.length > 0 && (
                      <ContextSelectorMenuMolecule
                        contexts={contexts}
                        selectedContext={selectedSearchContext}
                        onSelect={handleContextSelect}
                        loading={contextLoading}
                        className="nav-bar-search-context-selector"
                      />
                    )}
                    <input
                      type="text"
                      className="nav-bar-search-input"
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      placeholder={t('globalSearch.placeholder')}
                      autoFocus
                      aria-label="Global search input"
                    />
                    <button
                      type="button"
                      className="nav-bar-search-close"
                      onClick={handleSearchIconClick}
                      aria-label={t('globalSearch.close')}
                    >
                      <i className="pi pi-times" />
                    </button>
                  </div>
                  {(searchQuery.trim() !== '' || isSearching) && (
                    <div className="nav-bar-search-results">
                      <GlobalSearchResultsListMolecule
                        results={searchResults}
                        isSearching={isSearching}
                        error={searchError}
                        selectedIndex={selectedResultIndex}
                        onResultSelect={handleResultSelect}
                        noResultsText={t('globalSearch.noResults')}
                      />
                    </div>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  className="nav-bar-button nav-bar-search-icon"
                  onClick={handleSearchIconClick}
                  aria-label={t('globalSearch.open')}
                >
                  <i className="pi pi-search" />
                </button>
              )}
            </div>
            <PerspectiveMenu
              perspectives={sphere.perspectives ?? []}
              perspectiveSelected={handlePerspectiveSelected}
              className="nav-bar-button"
            />
            <MetaspaceMenu
              sphere={sphere}
              metaspaceSelected={handleMetaspaceSelected}
              className="nav-bar-button"
              isNavBar={true}
            />
            <StatusSelector
              className="nav-bar-button"
              status={sphere.status?.currentValue?.status || 'unknown'}
              sphereId={sphere.id}
              sphereName={sphere.properties?.name || ''}
              schemaType={sphere.properties?.type}
              statusPoint={sphere.status}
              onConfigureFactors={onConfigureStatusFactors}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetasphereNavBar;
